import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { classLabel } from './contexts';
import { must, useDb } from './db';

export interface Quiz {
  id: string;
  courseId: string;
  subject: string;
  classLabel: string;
  title: string;
  description: string | null;
  dueOn: string;
  showAnswers: boolean;
  questions: number;
}

export interface QuizAttempt {
  id: string;
  quizId: string;
  studentId: string;
  studentName?: string;
  score: number;
  maxScore: number;
  submittedAt: string;
  answers: Record<string, number[]>;
  /** how often and how long the student left the open test («чесний тест») */
  awayCount: number;
  awaySeconds: number;
  /** 2 after a retake; the first score is kept for the teacher */
  attemptNo: number;
  firstScore: number | null;
}

export interface QuizQuestion {
  id: string;
  position: number;
  text: string;
  multiple: boolean;
  options: string[];
}

/** A test is open until the end of its due date. */
export function isQuizOpen(quiz: Pick<Quiz, 'dueOn'>, today: string) {
  return quiz.dueOn >= today;
}

export function useQuizzes(filter: { courseId: string } | { schoolId: string }) {
  const db = useDb();
  return useQuery({
    queryKey: ['quizzes', filter],
    enabled: 'courseId' in filter ? filter.courseId !== '' : filter.schoolId !== '',
    queryFn: async () => {
      let query = db
        .from('quizzes')
        .select(
          'id, course_id, title, description, due_on, show_answers, quiz_questions (count), courses (group_label, subjects (name), classes (grade, letter))',
        )
        .not('published_at', 'is', null)
        .order('due_on', { ascending: false });
      query =
        'courseId' in filter
          ? query.eq('course_id', filter.courseId)
          : query.eq('school_id', filter.schoolId);
      return must(await query).map((r): Quiz => ({
        id: r.id,
        courseId: r.course_id,
        subject:
          (r.courses?.subjects?.name ?? '') +
          (r.courses?.group_label ? ` (${r.courses.group_label})` : ''),
        classLabel: r.courses?.classes ? classLabel(r.courses.classes) : '',
        title: r.title,
        description: r.description,
        dueOn: r.due_on,
        showAnswers: r.show_answers,
        questions: r.quiz_questions[0]?.count ?? 0,
      }));
    },
  });
}

/** Attempts the person may see: their own or their children's, or (staff) of a test. */
export function useQuizAttempts(filter: { quizId: string } | { studentId: string }) {
  const db = useDb();
  return useQuery({
    queryKey: ['quiz-attempts', filter],
    enabled: 'quizId' in filter ? filter.quizId !== '' : filter.studentId !== '',
    queryFn: async () => {
      let query = db
        .from('quiz_attempts')
        .select(
          'id, quiz_id, student_member_id, score, max_score, submitted_at, answers, away_count, away_seconds, attempt_no, first_score, school_members (first_name, last_name)',
        );
      query =
        'quizId' in filter
          ? query.eq('quiz_id', filter.quizId)
          : query.eq('student_member_id', filter.studentId);
      return must(await query).map((r): QuizAttempt => ({
        id: r.id,
        quizId: r.quiz_id,
        studentId: r.student_member_id,
        ...(r.school_members && {
          studentName: `${r.school_members.last_name} ${r.school_members.first_name}`,
        }),
        score: r.score,
        maxScore: r.max_score,
        submittedAt: r.submitted_at,
        answers: r.answers as Record<string, number[]>,
        awayCount: r.away_count,
        awaySeconds: r.away_seconds,
        attemptNo: r.attempt_no,
        firstScore: r.first_score,
      }));
    },
  });
}

export function useQuizQuestions(quizId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['quiz-questions', quizId],
    enabled: quizId !== undefined,
    queryFn: async () =>
      must(
        await db
          .from('quiz_questions')
          .select('id, position, text, multiple, options')
          .eq('quiz_id', quizId as string)
          .order('position'),
      ),
  });
}

/** Staff only: the right answers of a test (for the share of right answers per question). */
export function useQuizKeys(quizId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['quiz-keys', quizId],
    enabled: quizId !== undefined,
    queryFn: async () => {
      const questions = must(
        await db
          .from('quiz_questions')
          .select('id')
          .eq('quiz_id', quizId as string),
      ).map((q) => q.id);
      const keys = must(
        await db
          .from('quiz_answer_keys')
          .select('question_id, correct')
          .in('question_id', questions),
      );
      return Object.fromEntries(keys.map((k) => [k.question_id, k.correct]));
    },
  });
}

export interface ReviewItem {
  questionId: string;
  position: number;
  question: string;
  options: string[];
  correct: number[];
  chosen: number[];
}

export function useQuizReview(attemptId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['quiz-review', attemptId],
    enabled: attemptId !== undefined,
    retry: false,
    queryFn: async () =>
      must(await db.rpc('quiz_review', { p_attempt: attemptId as string })).map(
        (r): ReviewItem => ({
          questionId: r.question_id,
          position: r.pos,
          question: r.question,
          options: r.options,
          correct: r.correct,
          chosen: r.chosen,
        }),
      ),
  });
}

export interface QuizDraftQuestion {
  text: string;
  multiple: boolean;
  options: string[];
  correct: number[];
}

/** The same choice, whatever the order it was ticked in. */
export const sameAnswer = (a: ReadonlyArray<number>, b: ReadonlyArray<number>) =>
  a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);

export function useQuizActions() {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['quizzes'] });
    void client.invalidateQueries({ queryKey: ['quiz-attempts'] });
  };
  return {
    create: useMutation({
      mutationFn: async (d: {
        courseId: string;
        title: string;
        description: string;
        dueOn: string;
        showAnswers: boolean;
        questions: QuizDraftQuestion[];
      }) =>
        must(
          await db.rpc('create_quiz', {
            p_course: d.courseId,
            p_title: d.title,
            p_description: d.description,
            p_due_on: d.dueOn,
            p_show_answers: d.showAnswers,
            p_questions: d.questions.map((q) => ({
              text: q.text.trim(),
              multiple: q.multiple,
              options: q.options,
              correct: q.correct,
            })),
          }),
        ),
      onSettled: invalidate,
    }),
    /** `clientId` makes a repeated send (a phone retrying) return the first attempt. */
    submit: useMutation({
      mutationFn: async (a: {
        quizId: string;
        answers: Record<string, number[]>;
        clientId: string;
        away?: { count: number; seconds: number };
      }) => {
        const rows = must(
          await db.rpc('submit_quiz', {
            p_quiz: a.quizId,
            p_answers: a.answers,
            p_client_id: a.clientId,
            p_away_count: a.away?.count ?? 0,
            p_away_seconds: a.away?.seconds ?? 0,
          }),
        );
        return rows[0] as { score: number; max_score: number };
      },
      onSettled: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (quizId: string) =>
        must(await db.from('quizzes').delete().eq('id', quizId)),
      onSettled: invalidate,
    }),
    /** A teacher lets one student take the test again. */
    allowRetake: useMutation({
      mutationFn: async (a: { quizId: string; studentId: string }) =>
        must(await db.rpc('allow_quiz_retake', { p_quiz: a.quizId, p_student: a.studentId })),
      onSettled: () => client.invalidateQueries({ queryKey: ['quiz-retakes'] }),
    }),
  };
}

/** Open retakes: of a student (and the family), or of a test (staff). */
export function useQuizRetakes(filter: { studentId: string } | { quizId: string }) {
  const db = useDb();
  return useQuery({
    queryKey: ['quiz-retakes', filter],
    enabled: 'studentId' in filter ? filter.studentId !== '' : filter.quizId !== '',
    queryFn: async () => {
      let query = db.from('quiz_retakes').select('quiz_id, student_member_id').is('used_at', null);
      query =
        'studentId' in filter
          ? query.eq('student_member_id', filter.studentId)
          : query.eq('quiz_id', filter.quizId);
      return must(await query).map((r) => ({ quizId: r.quiz_id, studentId: r.student_member_id }));
    },
  });
}

/** «12 с», «2 хв 05 с» — how long somebody was away. */
export const awayTime = (seconds: number) =>
  seconds < 60
    ? `${seconds} с`
    : `${Math.floor(seconds / 60)} хв ${String(seconds % 60).padStart(2, '0')} с`;

/** How many students a course has (for «пройшли 18 з 25»). */
export function useCourseSize(courseId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['course-size', courseId],
    enabled: courseId !== undefined,
    queryFn: async () => {
      const { count, error } = await db
        .from('course_students')
        .select('student_member_id', { count: 'exact', head: true })
        .eq('course_id', courseId as string);
      if (error) throw error;
      return count ?? 0;
    },
  });
}
